import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Button,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Chip,
    Alert,
    Box,
    Skeleton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    People,
    Visibility,
    Group as GroupIcon
} from '@mui/icons-material';
import { Group } from '../../../Models/Group';
import { User } from '../../../Models/User';
import useCurrentUser from '../../../hooks/useCurrentUser';
import useAxios from '../../../hooks/useAxios';
import { ADMIN_VIEWS, DELETE_GROUP_URL, CLEAR_GROUP_MEMBERS_URL, GROUP_LIST_URL } from '../../../utils/constant';
import '../../../styles/AdminGroupsTab.css';
import GroupDetailsModal from "./GroupDetailsModal";
import { logger } from '../../../utils/logger';


interface AdminGroupsTabProps {
    selectedMenu: (view: number) => void;
    onEditGroup: (group: any) => void;
}

const AdminGroupsTab: React.FC<AdminGroupsTabProps> = ({ selectedMenu, onEditGroup }) => {
    const { currentUser } = useCurrentUser();
    const { getWithAxios, deleteWithAxios } = useAxios();

    // États
    const [groups, setGroups] = useState<Group[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Modal de confirmation
    const [openDialog, setOpenDialog] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState<string | null>(null);

    // Snackbar
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

    // Charger les groupes
    useEffect(() => {
        const loadGroups = async () => {
            if (!currentUser?.tokens?.accessToken) return;

            setIsLoading(true);
            setError(null);

            try {
                const groupData = await getWithAxios(GROUP_LIST_URL);
                setGroups(Array.isArray(groupData?.data) ? groupData.data : []);
            } catch (err) {
                logger.error('Erreur lors du chargement des groupes:', err);
                setError(err instanceof Error ? err.message : 'Erreur lors du chargement des groupes');
            } finally {
                setIsLoading(false);
            }
        };

        loadGroups();
    }, [currentUser]);
    // State pour détails
    const [openDetails, setOpenDetails] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
    const [selectedGroupMembers, setSelectedGroupMembers] = useState<User[]>([]);
    // Ouvrir le modal de suppression
    const openDeleteDialog = (groupUuid: string) => {
        setGroupToDelete(groupUuid);
        setOpenDialog(true);
    };

    // Confirmer suppression
    const confirmDeleteGroup = async () => {
        if (!groupToDelete || !currentUser?.tokens?.accessToken) return;

        try {
            await deleteWithAxios(DELETE_GROUP_URL(groupToDelete));
            setGroups(prev => prev.filter(group => group.uuid !== groupToDelete));

            setSnackbarMessage('Groupe supprimé avec succès !');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (err) {
            logger.error('Erreur lors de la suppression:', err);
            setSnackbarMessage(err instanceof Error ? err.message : 'Erreur lors de la suppression');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setOpenDialog(false);
            setGroupToDelete(null);
        }
    };

    // Créer un nouveau groupe
    const handleCreateGroup = () => selectedMenu(ADMIN_VIEWS.NEW_GROUP_VIEW);

    // Vider les membres d'un groupe
    const handleClearMembers = async (groupUuid: string) => {
        if (!currentUser?.tokens?.accessToken) return;

        try {
            const updatedGroup = await deleteWithAxios(CLEAR_GROUP_MEMBERS_URL(groupUuid));

            // Mettre à jour la liste des groupes
            setGroups(prev => prev.map(g =>
                g.uuid === groupUuid
                    ? { ...g, memberCount: 0, memberUuids: [] }
                    : g
            ));

            // Si le modal de détails est ouvert pour ce groupe, mettre à jour
            if (selectedGroup?.uuid === groupUuid) {
                setSelectedGroup({ ...selectedGroup, memberCount: 0, memberUuids: [] });
                setSelectedGroupMembers([]);
            }

            setSnackbarMessage('Tous les membres ont été retirés du groupe');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (err) {
            logger.error('Erreur lors du vidage du groupe:', err);
            setSnackbarMessage(err instanceof Error ? err.message : 'Erreur lors du vidage du groupe');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    // Voir les détails d'un groupe
    const handleViewGroup = async (group: Group) => {
        setSelectedGroup(group);
        try {
            // Appel API pour récupérer les membres du groupe
            const response = await getWithAxios(`/admin/groups/${group.uuid}/users`);
            setSelectedGroupMembers(Array.isArray(response.users) ? response.users : []);
        } catch (err) {
            logger.error("Erreur chargement membres:", err);
            setSelectedGroupMembers([]);
        }
        setOpenDetails(true);
    };





    // Render loading
    if (isLoading) {
        return (
            <div className="groups-container">
                <div className="groups-header">
                    <Skeleton variant="text" width={200} height={40} />
                    <Skeleton variant="rectangular" width={120} height={36} />
                </div>
                <Card>
                    <CardContent>
                        {[...Array(5)].map((_, index) => (
                            <Skeleton key={index} variant="rectangular" height={60} style={{ marginBottom: 8 }} />
                        ))}
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Render error
    if (error) {
        return (
            <div className="groups-container">
                <Alert severity="error" className="groups-error">{error}</Alert>
                <Button onClick={() => window.location.reload()}>Réessayer</Button>
            </div>
        );
    }

    // Render empty state
    if (groups.length === 0) {
        return (
            <div className="groups-container">
                <div className="groups-header">
                    <Typography variant="h4" className="groups-title">
                        <GroupIcon /> Gestion des Groupes
                    </Typography>
                    <Button variant="contained" startIcon={<Add />} onClick={handleCreateGroup} className="create-group-btn">Nouveau Groupe</Button>
                </div>
                <Card className="empty-state-card">
                    <CardContent className="empty-state-content">
                        <GroupIcon className="empty-state-icon" />
                        <Typography variant="h6">Aucun groupe créé</Typography>
                        <Typography variant="body2">Créez votre premier groupe pour organiser vos utilisateurs</Typography>
                        <Button variant="contained" startIcon={<Add />} onClick={handleCreateGroup} className="empty-state-button">Créer un groupe</Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="groups-container">
            <div className="groups-header">
                <Typography variant="h4" className="groups-title">
                    <GroupIcon /> Gestion des Groupes
                </Typography>
                <Button variant="contained" startIcon={<Add />} onClick={handleCreateGroup} className="create-group-btn">Nouveau Groupe</Button>
            </div>

            <Card className="groups-card">
                <CardContent>
                    <TableContainer component={Paper} className="groups-table-container">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Nom du Groupe</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Membres</TableCell>
                                    <TableCell>Créé le</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {groups.map(group => (
                                    <TableRow key={group.uuid} hover>
                                        <TableCell>
                                            <Box className="group-name-cell">
                                                <div className="group-avatar">{group.name.charAt(0).toUpperCase()}</div>
                                                <Typography variant="subtitle1">{group.name}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{group.description || 'Aucune description'}</TableCell>
                                        <TableCell>
                                            <Chip icon={<People />} label={`${group.memberCount ?? 0} membre(s)`} color="primary" variant="outlined" size="small" />
                                        </TableCell>
                                        <TableCell>{new Date(group.createdAt).toLocaleDateString('fr-FR')}</TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={() => handleViewGroup(group)} color="primary" title="Voir les détails"><Visibility /></IconButton>
                                            <IconButton onClick={() => onEditGroup(group)} color="default" title="Modifier"><Edit /></IconButton>
                                            <IconButton onClick={() => openDeleteDialog(group.uuid)} color="error" title="Supprimer"><Delete /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* Modal de confirmation */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>Confirmer la suppression</DialogTitle>
                <DialogContent>Êtes-vous sûr de vouloir supprimer ce groupe ? Cette action est irréversible.</DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Annuler</Button>
                    <Button color="error" onClick={confirmDeleteGroup}>Supprimer</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled">
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            <GroupDetailsModal
                open={openDetails}
                group={selectedGroup}
                members={selectedGroupMembers}
                onClose={() => setOpenDetails(false)}
                onEdit={(grp) => {
                    setOpenDetails(false);
                    onEditGroup(grp);
                }}
                onDelete={(uuid) => {
                    setOpenDetails(false);
                    openDeleteDialog(uuid);
                }}
                onClearMembers={handleClearMembers}
            />
        </div>
    );
};

export default AdminGroupsTab;
